import { useEffect, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ActionsListPage } from '@Components';
import { useCategories, useViewMoreProjectsList } from '@Hooks/Api';
import { RoutingPaths } from '@App/paths';
import { useUserContext } from '@Contexts';
import { UserTypeEnum } from '@Types';

enum CTATypeEnum {
  'UNLOGGED' = 'UNLOGGED',
  'FUNDER' = 'FUNDER',
  'GROUP' = 'GROUP',
}

const MAP_CTA_LINKS = {
  [CTATypeEnum.UNLOGGED]: RoutingPaths.SIGNUP_FUNDER,
  [CTATypeEnum.FUNDER]: RoutingPaths.DASHBOARD_FUNDER,
  [CTATypeEnum.GROUP]: RoutingPaths.DASHBOARD_GROUP,
};

export const ProjectsListPageController = () => {
  const {
    data: projectsList,
    loading: isProjectsListLoading,
    hasNextPage,
    fetchMoreProjects,
    isFetchMoreLoading,
  } = useViewMoreProjectsList();
  const [getCategories, { data: categories, loading: isCategoriesLoading }] = useCategories(true);

  useEffect(() => {
    getCategories();
  }, [getCategories]);

  const { t } = useTranslation('project');
  const { userData } = useUserContext();
  const [currCtaType, setCurrCtaType] = useState<CTATypeEnum>(CTATypeEnum.UNLOGGED);

  useEffect(() => {
    if (userData?.user_type === UserTypeEnum.FUNDER) {
      setCurrCtaType(CTATypeEnum.FUNDER);
    }
    if (userData?.user_type === UserTypeEnum.GROUP) {
      setCurrCtaType(CTATypeEnum.GROUP);
    }
  }, [userData?.user_type]);

  const handleFetchMoreProjects = useCallback(() => {
    fetchMoreProjects();
  }, [fetchMoreProjects]);

  const getCtaData = useCallback(
    (ctaType: CTATypeEnum) => ({
      headerTitle: t('common:projects_list_page.header.title'),
      headerSubtitle: t(`common:projects_list_page.header.${ctaType}.subtitle`),
      headerButton: t(`common:projects_list_page.header.${ctaType}.button`),
      cardText: t(`common:projects_list_page.cta_card.${ctaType}.text`),
      cardButtonText: t(`common:projects_list_page.cta_card.${ctaType}.button`),
      ctaLink: MAP_CTA_LINKS[ctaType],
    }),
    [t],
  );

  const ctaData = getCtaData(currCtaType);

  return (
    <ActionsListPage
      projectsList={projectsList}
      categoriesList={categories}
      fetchMore={handleFetchMoreProjects}
      isFetchMoreLoading={isFetchMoreLoading}
      hasNextPage={hasNextPage}
      isLoading={isProjectsListLoading || isCategoriesLoading}
      headerTitle={ctaData.headerTitle}
      headerSubtitle={ctaData.headerSubtitle}
      headerButton={ctaData.headerButton}
      cardText={ctaData.cardText}
      cardButtonText={ctaData.cardButtonText}
      ctaLink={ctaData.ctaLink}
    />
  );
};
